import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  getAssessmentHistory,
  getAssessmentsByGoal,
  deleteAssessment,
  getCreditPlan,
} from "../../services/assessmentService";
import { useAuth } from "../auth/AuthContext";
import { useTier } from "../../hooks/useTier";
import { useScope } from "../../hooks/useScope";
import { usePermissions } from "../../hooks/usePermissions";
import { useTranslation } from "../../hooks/useTranslation";
import {
  trackFeatureUsed,
  trackCTAClick,
  trackContentView,
} from "../../services/analyticsService";
import MarkdownRenderer from "./MarkdownRenderer";
import "./AssessmentHistory.css";

const AssessmentHistory = () => {
  const history = useHistory();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all"); // 'all', 'subcategory', 'text'
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [searchText, setSearchText] = useState("");
  const [navigating, setNavigating] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const { currentUser } = useAuth();
  const {
    hasFeature,
    tierId,
    loading: tierLoading,
    isInClientView,
  } = useTier();
  const { isClientView, isManaged, mentorId, effectiveUserId, actualUserId } =
    useScope();
  const { canDeletePlans } = usePermissions();
  const t = useTranslation();

  // Compute scope mode
  const isMentorMasquerade = isManaged && !isClientView;
  const isInClientViewMode = isManaged && isClientView;

  // Helper function to filter assessments by scope
  const filterByScope = useCallback(
    (assessmentList) => {
      if (!assessmentList || !Array.isArray(assessmentList)) return [];

      return assessmentList.filter((assessment) => {
        // Mentor masquerade: show assessments with mentorId matching the mentor (actualUserId)
        if (isMentorMasquerade && actualUserId) {
          return assessment.mentorId === actualUserId;
        }
        // Client view: show assessments with matching mentorId
        if (isInClientViewMode && mentorId) {
          return assessment.mentorId === mentorId;
        }
        // Personal view: show only assessments WITHOUT mentorId
        return !assessment.mentorId;
      });
    },
    [isMentorMasquerade, isInClientViewMode, mentorId, actualUserId]
  );

  // Check if user can view plans based on tier
  // mentorClient tier has can_view_plans: true
  // Regular paid tiers (basic, pro, elite) also have access
  const canViewPlans =
    hasFeature("can_view_plans") ||
    tierId === "basic" ||
    tierId === "pro" ||
    tierId === "elite";
  // Note: canDeletePlans is now provided by usePermissions hook which uses
  // the centralized permissions.js configuration based on the user's current role

  // Redirect to subscription page if tier doesn't allow viewing plans
  useEffect(() => {
    if (!tierLoading && currentUser && !canViewPlans) {
      history.push("/manage-subscription");
    }
  }, [tierLoading, currentUser, canViewPlans, history]);

  useEffect(() => {
    if (currentUser && canViewPlans && !tierLoading) {
      loadAssessments();
    }
  }, [
    currentUser,
    canViewPlans,
    tierLoading,
    filter,
    selectedSubcategory,
    searchText,
    isClientView,
    mentorId,
    effectiveUserId,
    isMentorMasquerade,
  ]);

  // Track page view when component loads
  useEffect(() => {
    if (currentUser && canViewPlans && !tierLoading) {
      trackContentView({
        section: "assessment_history",
        content_type: "page",
        additionalParams: {
          filter_type: filter,
          is_client_view: isInClientView,
        },
      });
    }
  }, [currentUser, canViewPlans, tierLoading, isInClientView]);

  const loadAssessments = async () => {
    try {
      setLoading(true);
      setError(null);

      // Determine if we need to pass targetUserId for mentor masquerade mode
      // When mentor is viewing client's data, effectiveUserId is the client's ID
      const targetUserId = isMentorMasquerade && effectiveUserId ? effectiveUserId : undefined;

      let result;
      if (filter === "subcategory" && selectedSubcategory) {
        // Special handling for custom_goal to avoid Firebase function issues
        if (selectedSubcategory === "custom_goal") {
          setFilterLoading(true);
          // Get all assessments and filter on client side
          result = await getAssessmentHistory({ limit: 100, targetUserId });
          const allAssessments =
            result.data?.assessments || result.assessments || [];
          const customGoalAssessments = allAssessments.filter(
            (assessment) =>
              assessment.subcategory === "custom_goal" ||
              assessment.form_type === "custom_goal_form"
          );
          result = {
            data: {
              assessments: customGoalAssessments,
              total: customGoalAssessments.length,
            },
          };
          setFilterLoading(false);
        } else {
          try {
            result = await getAssessmentsByGoal({
              subcategory: selectedSubcategory,
              targetUserId,
            });
          } catch (subcategoryError) {
            console.error(
              "Error filtering by subcategory, falling back to client-side filtering:",
              subcategoryError
            );
            // Fallback: get all assessments and filter on client side
            result = await getAssessmentHistory({ limit: 100, targetUserId });
            const allAssessments =
              result.data?.assessments || result.assessments || [];
            const filteredAssessments = allAssessments.filter(
              (assessment) => assessment.subcategory === selectedSubcategory
            );
            result = {
              data: {
                assessments: filteredAssessments,
                total: filteredAssessments.length,
              },
            };
          }
        }
      } else if (filter === "text" && searchText.trim()) {
        // Text search - get all assessments and filter on client side
        result = await getAssessmentHistory({ limit: 100, targetUserId });
        const allAssessments =
          result.data?.assessments || result.assessments || [];
        const searchLower = searchText.toLowerCase();
        const filteredAssessments = allAssessments.filter((assessment) => {
          const goalText = assessment.goal?.goal || "";
          const subcategoryText = assessment.subcategory || "";
          const analysisText = assessment.aiAnalysis || "";

          return (
            goalText.toLowerCase().includes(searchLower) ||
            subcategoryText.toLowerCase().includes(searchLower) ||
            analysisText.toLowerCase().includes(searchLower)
          );
        });
        result = {
          data: {
            assessments: filteredAssessments,
            total: filteredAssessments.length,
          },
        };
      } else {
        result = await getAssessmentHistory({ limit: 50, targetUserId });
      }

      const loadedAssessments =
        result.data?.assessments || result.assessments || [];

      // Filter assessments by scope (personal vs mentor view)
      const scopeFilteredAssessments = filterByScope(loadedAssessments);
      setAssessments(scopeFilteredAssessments);

      // Track successful load
      trackFeatureUsed({
        section: "assessment_history",
        feature: "load_assessments",
        action: "success",
        source: "page_load",
        additionalParams: {
          filter_type: filter,
          assessment_count: loadedAssessments.length,
          subcategory: filter === "subcategory" ? selectedSubcategory : null,
          search_text: filter === "text" ? searchText : null,
        },
      });
    } catch (err) {
      setError(err.message);
      console.error("Error loading assessments:", err);

      // Track error
      trackFeatureUsed({
        section: "assessment_history",
        feature: "load_assessments",
        action: "error",
        source: "page_load",
        additionalParams: {
          error: err.message,
          filter_type: filter,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAssessment = async (assessmentId) => {
    if (window.confirm(t("assessmentHistory.deleteConfirm"))) {
      // Track delete confirmation
      const assessment = assessments.find((a) => a.id === assessmentId);
      trackFeatureUsed({
        section: "assessment_history",
        feature: "delete_assessment",
        action: "confirm",
        source: "button",
        additionalParams: {
          assessment_id: assessmentId,
          subcategory: assessment?.subcategory,
          goal_id: assessment?.goalId,
        },
      });

      try {
        await deleteAssessment(assessmentId);

        // Track successful deletion
        trackFeatureUsed({
          section: "assessment_history",
          feature: "delete_assessment",
          action: "success",
          source: "button",
          additionalParams: {
            assessment_id: assessmentId,
            subcategory: assessment?.subcategory,
          },
        });

        // Reload assessments after deletion
        loadAssessments();
      } catch (err) {
        setError(err.message);
        console.error("Error deleting assessment:", err);

        // Track deletion error
        trackFeatureUsed({
          section: "assessment_history",
          feature: "delete_assessment",
          action: "error",
          source: "button",
          additionalParams: {
            assessment_id: assessmentId,
            error: err.message,
          },
        });
      }
    } else {
      // Track delete cancellation
      trackFeatureUsed({
        section: "assessment_history",
        feature: "delete_assessment",
        action: "cancel",
        source: "button",
        additionalParams: {
          assessment_id: assessmentId,
        },
      });
    }
  };

  const handleViewFullAnalysis = async (assessment) => {
    // Track view full analysis click
    trackCTAClick({
      section: "assessment_history",
      action: "view_full_analysis",
      source: "button",
      additionalParams: {
        assessment_id: assessment.id,
        subcategory: assessment.subcategory,
        goal_id: assessment.goalId,
        goal_title: assessment.goal?.goal,
        is_custom_goal: assessment.subcategory === "custom_goal",
      },
    });

    setNavigating(true);
    try {
      // For regular assessments, try to get the credit plan using the standard method
      // Use the same ID creation pattern as Firebase functions: ${uid}_${goalId}
      // The goalId should now be consistent between assessments and credit plans

      // Ensure we have a valid goalId - this is critical for finding the matching credit plan
      if (
        !assessment.goalId ||
        typeof assessment.goalId !== "string" ||
        assessment.goalId.trim() === ""
      ) {
        console.error(
          "Assessment missing or invalid goalId - cannot construct plan ID:",
          assessment
        );
        console.error("Assessment structure:", {
          id: assessment.id,
          subcategory: assessment.subcategory,
          formId: assessment.formId,
          goal: assessment.goal,
          userId: assessment.userId || assessment.user_id,
          goalId: assessment.goalId,
        });
        setError(t("assessmentHistory.assessmentIncomplete"));

        // Track error
        trackFeatureUsed({
          section: "assessment_history",
          feature: "view_full_analysis",
          action: "error",
          source: "button",
          additionalParams: {
            assessment_id: assessment.id,
            error: "missing_goal_id",
          },
        });
        return;
      }

      const planId = `${assessment.userId || assessment.user_id}_${
        assessment.goalId
      }`;
      console.log("Trying to load plan with ID:", planId);
      console.log("Assessment goalId:", assessment.goalId);
      console.log(
        "Assessment userId:",
        assessment.userId || assessment.user_id
      );
      console.log("Full assessment object:", assessment);

      try {
        const creditPlan = await getCreditPlan(planId);
        console.log("Successfully loaded credit plan:", creditPlan);

        // Track successful plan load
        trackFeatureUsed({
          section: "assessment_history",
          feature: "view_full_analysis",
          action: "plan_loaded",
          source: "button",
          additionalParams: {
            assessment_id: assessment.id,
            plan_id: planId,
            has_plan: !!creditPlan,
          },
        });
      } catch (planError) {
        console.warn(
          "Plan not found or error loading plan, will navigate anyway:",
          planError
        );
        // Track plan load failure but still navigate
        trackFeatureUsed({
          section: "assessment_history",
          feature: "view_full_analysis",
          action: "plan_load_failed",
          source: "button",
          additionalParams: {
            assessment_id: assessment.id,
            plan_id: planId,
            error: planError.message,
          },
        });
      }

      // Navigate to credit plan flow using URL with planId
      // The component will load the plan from the database using the planId in the URL
      history.push(`/creditplan/${planId}`);
    } catch (error) {
      console.error("Error in handleViewFullAnalysis:", error);
      console.error("Attempted planId:", planId);
      console.error("Assessment data used:", assessment);

      // Track error
      trackFeatureUsed({
        section: "assessment_history",
        feature: "view_full_analysis",
        action: "error",
        source: "button",
        additionalParams: {
          assessment_id: assessment.id,
          error: error.message,
        },
      });

      // Even if there's an error, try to navigate to the plan page
      // The component will handle the case where the plan doesn't exist
      if (planId) {
        history.push(`/creditplan/${planId}`);
      } else {
        setError(t("assessmentHistory.assessmentIncomplete"));
      }
    } finally {
      setNavigating(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return t("assessmentHistory.notAvailable");
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleToggleText = useCallback(
    (assessmentId) => {
      const assessment = assessments.find((a) => a.id === assessmentId);
      const wasExpanded = assessment?.showFullText || false;

      setAssessments((prevAssessments) =>
        prevAssessments.map((a) =>
          a.id === assessmentId ? { ...a, showFullText: !a.showFullText } : a
        )
      );

      // Track text toggle
      trackFeatureUsed({
        section: "assessment_history",
        feature: "toggle_analysis_text",
        action: wasExpanded ? "collapse" : "expand",
        source: "button",
        additionalParams: {
          assessment_id: assessmentId,
          subcategory: assessment?.subcategory,
        },
      });
    },
    [assessments]
  );

  const truncateText = (text, maxLength = 100) => {
    if (!text) return "";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  if (!currentUser) {
    return (
      <div className="assessment-history">
        <div className="auth-required">
          <h2>{t("assessmentHistory.authenticationRequired")}</h2>
          <p>{t("assessmentHistory.pleaseLogIn")}</p>
        </div>
      </div>
    );
  }

  // Show loading state while checking tier
  if (tierLoading) {
    return (
      <div className="assessment-history">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>{t("assessmentHistory.checkingSubscription")}</p>
        </div>
      </div>
    );
  }

  // Show redirect message if tier doesn't allow viewing plans (will redirect via useEffect, but show message briefly)
  if (!canViewPlans) {
    return (
      <div className="assessment-history">
        <div className="auth-required">
          <h2>{t("assessmentHistory.premiumRequired")}</h2>
          <p>{t("assessmentHistory.redirecting")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-history">
      <div className="history-header">
        <h1>{t("assessmentHistory.title")}</h1>
        <p>{t("assessmentHistory.subtitle")}</p>
      </div>

      {/* Filters */}
      <div className="history-filters">
        <div className="filter-group">
          <label>{t("assessmentHistory.filterBy")}</label>
          <select
            value={filter}
            onChange={(e) => {
              const newFilter = e.target.value;
              trackFeatureUsed({
                section: "assessment_history",
                feature: "filter_change",
                action: "change",
                source: "select",
                additionalParams: {
                  filter_type: newFilter,
                  previous_filter: filter,
                },
              });
              setFilter(newFilter);
            }}
            className="filter-select"
          >
            <option value="all">{t("assessmentHistory.allAssessments")}</option>
            <option value="subcategory">
              {t("assessmentHistory.byCategory")}
            </option>
            <option value="text">
              {t("assessmentHistory.freeTextSearch")}
            </option>
          </select>
        </div>

        {filter === "subcategory" && (
          <div className="filter-group">
            <label>{t("assessmentHistory.category")}</label>
            <select
              value={selectedSubcategory}
              onChange={(e) => {
                const newSubcategory = e.target.value;
                trackFeatureUsed({
                  section: "assessment_history",
                  feature: "subcategory_filter",
                  action: "select",
                  source: "select",
                  additionalParams: {
                    subcategory: newSubcategory,
                    previous_subcategory: selectedSubcategory,
                  },
                });
                setSelectedSubcategory(newSubcategory);
              }}
              className="filter-select"
            >
              <option value="">{t("assessmentHistory.selectCategory")}</option>
              <option value="personal_credit_optimization">
                {t("assessmentHistory.personalCreditOptimization")}
              </option>
              <option value="business_capital_acquisition">
                {t("assessmentHistory.businessCapitalAcquisition")}
              </option>
              <option value="cash_flow_management_goals">
                {t("assessmentHistory.cashFlowManagement")}
              </option>
              <option value="business_growth_goals">
                {t("assessmentHistory.businessGrowth")}
              </option>
              <option value="risk_mitigation_goals">
                {t("assessmentHistory.riskMitigation")}
              </option>
              <option value="wealth_building_goals">
                {t("assessmentHistory.wealthBuilding")}
              </option>
              <option value="custom_goal">
                {t("assessmentHistory.customGoals")}
              </option>
            </select>
          </div>
        )}

        {filter === "text" && (
          <div className="filter-group">
            <label>{t("assessmentHistory.search")}</label>
            <input
              type="text"
              value={searchText}
              onChange={(e) => {
                const newSearchText = e.target.value;
                setSearchText(newSearchText);

                // Track search input (debounced - only track when user stops typing)
                // We'll track on blur or when filter is applied
              }}
              onBlur={() => {
                if (searchText.trim()) {
                  trackFeatureUsed({
                    section: "assessment_history",
                    feature: "text_search",
                    action: "search",
                    source: "input",
                    additionalParams: {
                      search_text: searchText.trim(),
                      search_length: searchText.trim().length,
                    },
                  });
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && searchText.trim()) {
                  trackFeatureUsed({
                    section: "assessment_history",
                    feature: "text_search",
                    action: "search",
                    source: "input",
                    additionalParams: {
                      search_text: searchText.trim(),
                      search_length: searchText.trim().length,
                    },
                  });
                }
              }}
              placeholder={t("assessmentHistory.searchPlaceholder")}
              className="filter-input"
            />
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message">
          <p>
            {t("assessmentHistory.error")} {error}
          </p>
          <button
            onClick={() => {
              trackFeatureUsed({
                section: "assessment_history",
                feature: "retry_load",
                action: "click",
                source: "button",
                additionalParams: {
                  error: error,
                },
              });
              loadAssessments();
            }}
            className="retry-button"
          >
            {t("assessmentHistory.retry")}
          </button>
        </div>
      )}

      {/* Loading State */}
      {(loading || filterLoading) && (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>
            {filterLoading
              ? t("assessmentHistory.filteringCustomGoals")
              : t("assessmentHistory.loadingAssessments")}
          </p>
        </div>
      )}

      {/* Assessments List */}
      {!loading && !filterLoading && !error && (
        <>
          {filter === "subcategory" &&
            selectedSubcategory === "custom_goal" &&
            assessments.length > 0 && (
              <div className="filter-info">
                <p>
                  {t("assessmentHistory.showingCustomGoals")
                    .replace("{count}", assessments.length)
                    .replace("{plural}", assessments.length !== 1 ? "s" : "")}
                </p>
              </div>
            )}

          {filter === "text" && searchText.trim() && assessments.length > 0 && (
            <div className="filter-info">
              <p>
                {t("assessmentHistory.foundResults")
                  .replace("{count}", assessments.length)
                  .replace("{plural}", assessments.length !== 1 ? "s" : "")
                  .replace("{search}", searchText)}
              </p>
            </div>
          )}

          <div className="assessments-list">
            {assessments.length === 0 ? (
              <div className="empty-state">
                <h3>{t("assessmentHistory.noAssessmentsFound")}</h3>
                <p>
                  {filter === "subcategory" &&
                  selectedSubcategory === "custom_goal"
                    ? t("assessmentHistory.noCustomGoalsYet")
                    : filter === "text" && searchText.trim()
                    ? t("assessmentHistory.noSearchResults").replace(
                        "{search}",
                        searchText
                      )
                    : t("assessmentHistory.noAssessmentsYet")}
                </p>
              </div>
            ) : (
              assessments.map((assessment) => (
                <div
                  key={assessment.id}
                  className={`assessment-card ${
                    assessment.subcategory === "custom_goal"
                      ? "custom-goal-card"
                      : ""
                  }`}
                >
                  <div className="assessment-header">
                    <h3 className="assessment-title">
                      {assessment.subcategory === "custom_goal" ? "🎯 " : ""}
                      {assessment.goal?.goal ||
                        t("assessmentHistory.assessment")}
                    </h3>
                    <span className="assessment-date">
                      {formatDate(assessment.submittedAt)}
                    </span>
                  </div>

                  <div className="assessment-meta">
                    <div className="meta-item">
                      <span className="meta-label">
                        {t("assessmentHistory.categoryLabel")}
                      </span>
                      <span className="meta-value">
                        {assessment.subcategory}
                      </span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">
                        {t("assessmentHistory.timelineLabel")}
                      </span>
                      <span className="meta-value">
                        {assessment.goal?.timeline ||
                          t("assessmentHistory.notAvailable")}
                      </span>
                    </div>
                  </div>

                  {assessment.aiAnalysis && (
                    <div className="assessment-preview">
                      <div className="preview-title">
                        {t("assessmentHistory.aiAnalysisPreview")}
                      </div>
                      <div className="preview-content">
                        <MarkdownRenderer
                          content={assessment.aiAnalysis}
                          showFullText={assessment.showFullText}
                          maxLength={400}
                          onToggleText={() => handleToggleText(assessment.id)}
                          showToggleButton={true}
                        />
                      </div>
                    </div>
                  )}

                  <div className="assessment-actions">
                    <button
                      className="action-btn view-btn"
                      onClick={() => handleViewFullAnalysis(assessment)}
                      disabled={navigating}
                    >
                      {navigating
                        ? t("assessmentHistory.loading")
                        : t("assessmentHistory.viewFullAnalysis")}
                    </button>
                    {canDeletePlans && (
                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDeleteAssessment(assessment.id)}
                        disabled={navigating}
                      >
                        {t("assessmentHistory.deleteAssessment")}
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AssessmentHistory;
